import * as ICAL from 'ical.js'

import {
    EventrDate,
    getUserOrgPref,
    getUserPref,
    setUserOrgPref,
    spLogTags,
} from '@eventr/common'
import { isAndroid, isIOS } from 'react-device-detect'
import {
    calErrorMessage,
    calIdGlobal,
    calPerm,
    syncing,
} from '../../state/calendarState/calendarSignal'

import { Capacitor } from '@capacitor/core'
import { DateTime } from 'luxon'
import { bs } from '../../state/bound.store'
import { compareBuildVersion } from '../../utils'
import { EventrCapacitorCalendar } from '../push/plugin'

type Event = {
    calendarId: string
    title: string
    location?: string
    notes?: string
    startDate: number
    endDate: number
    alertOffsetInMinutes?: number
    reminderDetails?: ReminderDetails
    reminderEnabled?: boolean
}

interface ReminderDetails {
    title?: string
    listId?: string
    priority?: number
    isCompleted?: boolean
    startDate?: number
    dueDate?: number
    notes?: string
    url?: string
    location?: string
}

let isUpdatingCalendar = false

export const syncDeviceCalendar = async (
    calendar: string,
    orgName: string,
    cals: string[],
    update?: boolean,
    reminder?: boolean
) => {
    if (!Capacitor.isNativePlatform()) return null
    const selectedItinID = bs().selectedItinID
    const currentCals = cals
        ? cals
        : getUserOrgPref('homepage', 'calendarSync', orgName)

    const itinId = `itinId: ${selectedItinID.toString()} -`
    if (!update) {
        createCalendar(calendar, orgName, cals, reminder)
    } else if (calPerm.value && currentCals) {
        const itinVersion = findItineraryVersion()
        const currentCal = currentCals?.find((element: string | string[]) => {
            return element.includes(itinId)
        })
        const regex = /itinVersion:\s*(\d+)/
        const calVersion = currentCal?.match(regex)[1]
        const reminderEnabled = getUserPref(
            'account',
            'calendarReminderEnabled'
        )
        if (calVersion !== itinVersion) {
            updateCalendar(
                calendar,
                orgName,
                currentCal,
                currentCals,
                reminderEnabled
            )
        }
    }
}

const updateCalendar = async (
    calendar: string,
    orgName: string,
    cal: string,
    cals?: string[],
    reminder?: boolean
) => {
    if (isUpdatingCalendar) return
    isUpdatingCalendar = true

    try {
        spLogTags(
            `Org Name: ${orgName}\nCalendar: ${cal}\nUpdating Calendar...`,
            ['CALENDAR']
        )
        if (!cal) {
            console.error('Calendar not found')
            isUpdatingCalendar = false
            return
        }

        const foundCal = await findCalendarById(cal.split(' - ')[1])
        if (foundCal) {
            await EventrCapacitorCalendar.deleteCalendar({ id: foundCal })

            const appendedCal = (cals ?? []).filter((element) => {
                return !element.includes(foundCal)
            })
            setUserOrgPref('homepage', `calendarSync`, appendedCal, orgName)
            createCalendar(calendar, orgName, appendedCal, reminder)
        }
    } finally {
        isUpdatingCalendar = false
    }
}

const createCalendar = (
    calendar: string,
    orgName: string,
    cals: string[],
    reminder?: boolean
) => {
    try {
        const ical = ICAL.parse(calendar)

        const calendarSummaryTitle = ical[1][5][3]
        EventrCapacitorCalendar.createCalendar({
            title: 'Eventr - ' + calendarSummaryTitle,
            color: getRandomColor(),
        })
            .then((createdCalResponse) => {
                const createdCalId = createdCalResponse.result

                calIdGlobal.value = {
                    id: createdCalId,
                    title: 'Eventr - ' + calendarSummaryTitle,
                }
                return EventrCapacitorCalendar.listCalendars()
            })
            .then((allCalendarsResponse) => {
                const allCalendars = allCalendarsResponse.result

                if (!allCalendars.includes(calIdGlobal.value)) {
                    syncing.value = true
                } else {
                    syncing.value = false
                }
                const selectedItinID = bs().selectedItinID
                const calId = `itinVersion: ${findItineraryVersion()} itinId: ${selectedItinID.toString()} - ${
                    calIdGlobal.value.id
                }`

                cals?.push(calId)

                setUserOrgPref('homepage', `calendarSync`, cals, orgName)

                const events = ical[2]
                const title = 3
                const description = 5
                const descriptionName = 3
                const checkIn = 6
                const checkInTime = 3
                const startDate = 0
                const endDate = 1

                spLogTags(
                    `Org Name: ${orgName}\nCalendar: ${calId}\nEvents are now generating`,
                    ['CALENDAR']
                )

                const createEventPromises = events.map((event: any) => {
                    const eventInfo = event[1]

                    const eventTitle = eventInfo[title][descriptionName]
                    const eventStartDate = new EventrDate(
                        eventInfo[startDate][3]
                    )

                    const eventEndDate = DateTime.fromISO(eventInfo[endDate][3])
                        .isValid
                        ? new EventrDate(eventInfo[endDate][3])
                        : getAddHour(eventStartDate.getCloneEventrDate())

                    const userDefaultRemindTime = getUserPref(
                        'account',
                        'calendarReminders'
                    )
                    const checkInTimeArray = eventInfo[checkIn]
                        ? eventInfo[checkIn][checkInTime]
                        : ''
                    const newEvent: Event = {
                        calendarId: calIdGlobal.value.id ?? '',
                        title: eventTitle,
                        location: '',
                        notes: eventInfo[description][descriptionName] ?? '',
                        startDate: eventStartDate.getUnixMs,
                        endDate: eventEndDate.getUnixMs,
                        alertOffsetInMinutes: getReminderTime(
                            checkInTimeArray,
                            eventStartDate,
                            userDefaultRemindTime
                        ),
                        reminderEnabled: reminder ?? false,
                    }

                    return EventrCapacitorCalendar.createEvent(newEvent).catch(
                        (error) => {
                            spLogTags(
                                `Org Name: ${orgName}\nCalendar: ${calId}\nFailed to create event: ${eventTitle}. Error: ${
                                    error.message ?? ''
                                }`,
                                ['CALENDAR']
                            )
                        }
                    )
                })

                return Promise.all(createEventPromises)
            })
            .catch((error) => {
                syncing.value = false
                calErrorMessage.value = error.message
                console.error('Error creating calendar or events: ', error)
            })
    } catch (e) {
        spLogTags(`Failed to create calendar for org: ${orgName}`, ['CALENDAR'])
        syncing.value = false
        console.error('Error in parsing or processing: ', e)
    }
    return true
}

const getAddHour = (date: EventrDate) => {
    date.add({ hours: 1 })
    return date
}

const getRandomColor = () => {
    const letters = '0123456789ABCDEF'
    let color = '#'
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
    }
    return color
}

export const deleteCalendar = async (id: string, cal?: string) => {
    spLogTags(`Calendar: ${cal}\nIs now being deleted`, ['CALENDAR'])
    try {
        await EventrCapacitorCalendar.deleteCalendar({
            id: id,
        })
    } catch (e) {
        console.error('Error deleting events', e)
    }
    return
}

export function findItineraryVersion() {
    const itinerary = bs().selectedItinerary
    const itineraryVersion = bs().selectedItinerary?.version
    return itinerary ? itineraryVersion : null
}

export async function findCalendarById(targetId: string) {
    try {
        const { result: calendars } =
            await EventrCapacitorCalendar.listCalendars()
        const foundCalendar = calendars?.find((calendar) => {
            return calendar.id === targetId
        })

        if (foundCalendar) {
            return foundCalendar.id
        } else {
            return null
        }
    } catch (error) {
        console.error('Error fetching calendars:', error)
    }
}

const getIOSVersion = () => {
    if (
        navigator.userAgent.includes('iPhone') ||
        navigator.userAgent.includes('iPad')
    ) {
        const userAgent = navigator.userAgent

        const match = userAgent.match(/OS (\d+)_?(\d+)?/)

        if (match) {
            const majorVersion = parseInt(match[1], 10)
            const minorVersion = match[2] ? parseInt(match[2], 10) : 0
            return majorVersion + minorVersion / 10
        }
    }
    return null
}

const isBelowIOS17 = () => {
    const iosVersion = getIOSVersion()
    if (iosVersion !== null && iosVersion < 17) {
        spLogTags(`Phone is below iOS 17, calendar feature is disabled`, [
            'CALENDAR',
        ])
        return true
    } else {
        spLogTags(
            `Phone is above iOS 16 and below, calendar feature is enabled`,
            ['CALENDAR']
        )
        return false
    }
}

export const isCalendarEnabled = (version: string) => {
    const featureEnabled = compareBuildVersion(version, '1.4.0')
    if (Capacitor.isNativePlatform()) {
        if (isAndroid) {
            return featureEnabled
        } else if (isIOS) {
            return featureEnabled && !isBelowIOS17()
        }
    } else return false
}

const getReminderTime = (
    checkIn: string,
    startDate: EventrDate,
    userDefaultRemindTime: number
) => {
    if (checkIn !== '' || undefined) {
        const checkInDate = new EventrDate(checkIn)
        const isBehind = checkInDate.getUnixMs < startDate.getUnixMs
        const defaultTime = userDefaultRemindTime ? userDefaultRemindTime : 15
        return isBehind
            ? Math.ceil(
                  (startDate.getUnixMs - checkInDate.getUnixMs) / (1000 * 60)
              )
            : defaultTime === 0 || ''
            ? 15
            : userDefaultRemindTime
    } else return 15
}
